/* Paris Mall — mouvement (apparition, parallaxe, pointeur). Repris du design system. */
(function(){
"use strict";
var reducedMotion = function () { return typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches; };
// ---------------------------------------------------------------------------
// Mouvement : apparition au défilement, parallaxe, effets liés au pointeur
// Amélioration progressive : sans JavaScript, sans IntersectionObserver ou avec
// « réduire les animations », tout est affiché tel quel, rien n'est caché.
// ---------------------------------------------------------------------------
const MOTION = { started: false, roots: new Map(), parallax: new Set(), ticking: false };
const finePointer = () => !!window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
function motionRoot(el) {
    return el.closest('[data-motion-root]') || null;
}
function revealObserver(root) {
    const key = root || 'viewport';
    if (MOTION.roots.has(key))
        return MOTION.roots.get(key);
    const io = new window.IntersectionObserver((entries) => {
        const shown = entries
            .filter((e) => e.isIntersecting)
            .map((e) => e.target)
            .sort((a, b) => (a.compareDocumentPosition(b) & 4 ? -1 : 1));
        shown.forEach((el, i) => {
            el.style.setProperty('--pm-reveal-i', String(Math.min(i, 5)));
            el.classList.add('is-revealed');
            io.unobserve(el);
        });
    }, { root, rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
    MOTION.roots.set(key, io);
    return io;
}
function scanMotion() {
    const doc = window.document;
    doc.querySelectorAll('[data-reveal]:not([data-reveal-seen])').forEach((el) => {
        el.setAttribute('data-reveal-seen', '');
        if (el.closest('[data-motion="off"]'))
            return;
        const root = motionRoot(el);
        const box = el.getBoundingClientRect();
        const view = root ? root.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
        // Déjà à l'écran au chargement : affiché tout de suite, sans animation (rien ne retarde le premier affichage).
        if (box.top < view.bottom - (view.bottom - view.top) * 0.06 && box.bottom > view.top)
            return;
        el.classList.add('is-pending');
        revealObserver(root).observe(el);
    });
    doc.querySelectorAll('[data-parallax]:not([data-parallax-seen])').forEach((el) => {
        el.setAttribute('data-parallax-seen', '');
        MOTION.parallax.add(el);
    });
    if (MOTION.parallax.size)
        requestParallax();
}
function requestParallax() {
    if (MOTION.ticking)
        return;
    MOTION.ticking = true;
    window.requestAnimationFrame(() => {
        MOTION.ticking = false;
        const wide = window.innerWidth >= 768;
        MOTION.parallax.forEach((el) => {
            if (!el.isConnected) {
                MOTION.parallax.delete(el);
                return;
            }
            if (!wide) {
                el.style.removeProperty('--pm-py');
                return;
            }
            const root = motionRoot(el);
            const view = root ? root.getBoundingClientRect() : { top: 0, height: window.innerHeight };
            const box = el.getBoundingClientRect();
            if (box.bottom < view.top - 200 || box.top > view.top + view.height + 200)
                return;
            const speed = parseFloat(el.getAttribute('data-parallax')) || 0.06;
            const offset = box.top + box.height / 2 - (view.top + view.height / 2);
            el.style.setProperty('--pm-py', clamp(-offset * speed, -28, 28).toFixed(1) + 'px');
        });
    });
}
let pointerFrame = 0;
let lastPointer = null;
function onPointer(e) {
    lastPointer = e;
    if (pointerFrame)
        return;
    pointerFrame = window.requestAnimationFrame(() => {
        pointerFrame = 0;
        const ev = lastPointer;
        const t = ev.target && ev.target.closest ? ev.target : null;
        if (!t)
            return;
        const glow = t.closest('[data-cursor="glow"]');
        if (glow) {
            const b = glow.getBoundingClientRect();
            glow.style.setProperty('--pm-cx', (ev.clientX - b.left).toFixed(0) + 'px');
            glow.style.setProperty('--pm-cy', (ev.clientY - b.top).toFixed(0) + 'px');
            glow.classList.add('is-pointer');
        }
        const mag = t.closest('[data-magnetic]');
        if (mag) {
            const b = mag.getBoundingClientRect();
            const dx = ev.clientX - (b.left + b.width / 2);
            const dy = ev.clientY - (b.top + b.height / 2);
            mag.style.setProperty('--pm-mx', clamp(dx * 0.18, -6, 6).toFixed(1) + 'px');
            mag.style.setProperty('--pm-my', clamp(dy * 0.25, -4, 4).toFixed(1) + 'px');
        }
        const tilt = t.closest('[data-tilt]');
        if (tilt) {
            const b = tilt.getBoundingClientRect();
            const px = (ev.clientX - b.left) / b.width - 0.5;
            const py = (ev.clientY - b.top) / b.height - 0.5;
            tilt.style.setProperty('--pm-ry', (px * 8).toFixed(2) + 'deg');
            tilt.style.setProperty('--pm-rx', (-py * 6).toFixed(2) + 'deg');
        }
    });
}
function onPointerOut(e) {
    const from = e.target && e.target.closest ? e.target : null;
    if (!from)
        return;
    const to = e.relatedTarget;
    ['[data-cursor="glow"]', '[data-magnetic]', '[data-tilt]'].forEach((sel) => {
        const el = from.closest(sel);
        if (el && !(to && el.contains(to))) {
            el.classList.remove('is-pointer');
            ['--pm-mx', '--pm-my', '--pm-rx', '--pm-ry'].forEach((p) => el.style.removeProperty(p));
        }
    });
}
/** Lance le mouvement du site. Appelé une fois, au chargement du bundle ; sans effet si l'utilisateur réduit les animations. */
function startMotion() {
    if (typeof window === 'undefined' || MOTION.started)
        return;
    MOTION.started = true;
    if (reducedMotion() || !('IntersectionObserver' in window) || !('MutationObserver' in window))
        return;
    const doc = window.document;
    doc.documentElement.classList.add('pm-motion');
    let pending = false;
    const schedule = () => {
        if (pending)
            return;
        pending = true;
        window.requestAnimationFrame(() => {
            pending = false;
            scanMotion();
        });
    };
    new window.MutationObserver(schedule).observe(doc.documentElement, { childList: true, subtree: true });
    schedule();
    window.addEventListener('scroll', requestParallax, { passive: true, capture: true });
    window.addEventListener('resize', requestParallax, { passive: true });
    if (finePointer()) {
        doc.addEventListener('pointermove', onPointer, { passive: true });
        doc.addEventListener('pointerout', onPointerOut, { passive: true });
    }
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", startMotion); else startMotion();
})();
